/**
 * Persisted map viewport, scoped per scenario.
 *
 * Scoping is not a nicety. A player is in several scenarios at once, each with a
 * different home node and a different region of the world in play. On a single
 * global key, opening scenario B restores scenario A's centre and zoom — and it
 * never recovers, because the null sentinel means "never set" and the home-node
 * fallback only fires on genuine first visit.
 *
 * Shaped as an object keyed by scenario id rather than one storage key per
 * scenario, matching planningModeByScenario and selectedScenarioBySub elsewhere
 * in this codebase.
 *
 * Pure module: takes and returns plain objects, touches no storage itself.
 */

/** "Never set" — the map falls back to centring on the player's home node. */
export const NULL_VIEWPORT = Object.freeze({ lat: null, lon: null, zoom: null })

function isValid(viewport) {
  return !!viewport
    && Number.isFinite(viewport.lat)
    && Number.isFinite(viewport.lon)
    && Number.isFinite(viewport.zoom)
}

/**
 * @param {Object<string, {lat: number, lon: number, zoom: number}>} byScenario
 * @param {string|null} scenarioId
 * @returns {{lat: number|null, lon: number|null, zoom: number|null}}
 */
export function readViewport(byScenario, scenarioId) {
  if (!scenarioId || !byScenario) return NULL_VIEWPORT
  const stored = byScenario[scenarioId]
  return isValid(stored) ? { lat: stored.lat, lon: stored.lon, zoom: stored.zoom } : NULL_VIEWPORT
}

/**
 * @param {Object} byScenario
 * @param {string|null} scenarioId
 * @param {{lat: number, lon: number, zoom: number}} viewport
 * @returns {Object} a new object; the input is never mutated
 */
export function writeViewport(byScenario, scenarioId, viewport) {
  const base = byScenario ?? {}
  if (!scenarioId || !isValid(viewport)) return { ...base }
  return { ...base, [scenarioId]: { lat: viewport.lat, lon: viewport.lon, zoom: viewport.zoom } }
}
